import React, { useState, useEffect } from 'react';
import { Calendar, Award, Gift, DollarSign, ChevronDown, ChevronUp, Download, Info } from 'lucide-react';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

const inputCls =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

// ─── Shell reutilizable ────────────────────────────────────────────────────────
function SectionShell({ icon: Icon, iconCls, title, subtitle, value, isExpanded, onToggle, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={15} className={iconCls} />
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">RD$ {fmt(value)}</span>
          {isExpanded
            ? <ChevronUp size={14} className="text-gray-400" />
            : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-3">{children}</div>
        </div>
      )}
    </div>
  );
}

const TIPOS_VAC = [
  { value: 'total',      label: 'Vacaciones Total',           hint: 'Salario ÷ 23.83 × días asignados por política' },
  { value: 'prima',      label: 'Prima vacacional',           hint: 'Monto total − (Salario ÷ 2)' },
  { value: 'diferencia', label: 'Días disponibles (balance)', hint: 'Salario ÷ 23.83 × (días asignados − días usados)' },
];

// ─── Vacaciones ───────────────────────────────────────────────────────────────
export function VacacionesSection({ value, detalle, onUpdate, onLoad, loadingVac, vacInfo }) {
  const [isExpanded,   setIsExpanded]   = useState(false);
  const [monto,        setMonto]        = useState(value || 0);
  const [concepto,     setConcepto]     = useState(detalle?.concepto || '');
  const [tipoCalculo,  setTipoCalculo]  = useState('total');

  // Sync from external updates (e.g. auto-load)
  useEffect(() => { setMonto(value || 0); }, [value]);
  useEffect(() => { setConcepto(detalle?.concepto || ''); }, [detalle]);

  const handleMontoChange = (v) => { setMonto(v); onUpdate?.(v, { concepto }); };
  const handleConceptoChange = (v) => { setConcepto(v); onUpdate?.(monto, { concepto: v }); };

  const tipoActual = TIPOS_VAC.find(t => t.value === tipoCalculo);

  const BREAKDOWN_LABELS = {
    total:      'Salario/23.83 × días',
    prima:      '(Sal/23.83 − Sal/30) × días',
    diferencia: 'Salario/23.83 × días disponibles',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header siempre visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Calendar size={15} className="text-blue-500" />
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900">Vacaciones</p>
            <p className="text-xs text-gray-400">Pago por vacaciones disfrutadas</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">RD$ {fmt(value)}</span>
          {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>

      {/* Carga automática — siempre visible si onLoad está presente */}
      {onLoad && (
        <div className="px-4 pb-3 border-t border-gray-100 pt-3 space-y-2">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className={labelCls}>Tipo de cálculo</label>
              <select
                value={tipoCalculo}
                onChange={e => setTipoCalculo(e.target.value)}
                className={inputCls}
              >
                {TIPOS_VAC.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => onLoad(tipoCalculo)}
              disabled={loadingVac}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingVac
                ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-600" />
                : <Download size={12} />}
              {loadingVac ? 'Calculando...' : 'Cargar'}
            </button>
          </div>
          {tipoActual && (
            <p className="text-xs text-gray-400">{tipoActual.hint}</p>
          )}
          {vacInfo && (
            <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 space-y-1">
              <div className="flex items-center gap-1.5 font-semibold">
                <Info size={11} />
                {vacInfo.tipo_pago === 'prima' ? 'Prima vacacional' : vacInfo.tipo_pago === 'diferencia' ? 'Días disponibles' : 'Pago total'}
                {' — '}{vacInfo.dias} día(s)
              </div>
              <div className="text-blue-600 space-y-0.5">
                <p>Salario: <span className="font-medium">RD$ {fmt(vacInfo.salario)}</span></p>
                <p>Vacaciones Total: <span className="font-medium">RD$ {fmt(vacInfo.monto_total)}</span></p>
                {vacInfo.tipo_pago === 'prima' && (
                  <p>Prima (Total − Sal÷2): <span className="font-medium">RD$ {fmt(vacInfo.prima)}</span></p>
                )}
              </div>
              <div className="font-bold text-blue-900 border-t border-blue-200 pt-1 mt-1">
                A pagar: RD$ {fmt(vacInfo.monto)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campos manuales — dentro del panel expandible */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          <div>
            <label className={labelCls}>Monto (RD$)</label>
            <input
              type="number" step="0.01" min="0"
              value={monto || ''} placeholder="0.00"
              onChange={(e) => handleMontoChange(parseFloat(e.target.value) || 0)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Concepto (opcional)</label>
            <input
              type="text" value={concepto}
              onChange={(e) => handleConceptoChange(e.target.value)}
              placeholder="Ej: 15 días de vacaciones"
              className={inputCls}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Incentivos ───────────────────────────────────────────────────────────────
export function IncentivosSection({ value, detalle, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [monto,    setMonto]    = useState(value || 0);
  const [concepto, setConcepto] = useState(detalle?.concepto || '');

  const handleMontoChange    = (v) => { setMonto(v); onUpdate?.(v, { concepto }); };
  const handleConceptoChange = (v) => { setConcepto(v); onUpdate?.(monto, { concepto: v }); };

  return (
    <SectionShell
      icon={Award} iconCls="text-yellow-500"
      title="Incentivos" subtitle="Bonos por desempeño y reconocimientos"
      value={value} isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Monto (RD$)</label>
          <input
            type="number" step="0.01" min="0"
            value={monto || ''} placeholder="0.00"
            onChange={(e) => handleMontoChange(parseFloat(e.target.value) || 0)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Concepto (opcional)</label>
          <input
            type="text" value={concepto}
            onChange={(e) => handleConceptoChange(e.target.value)}
            placeholder="Ej: Incentivo por desempeño Q1"
            className={inputCls}
          />
        </div>
      </div>
    </SectionShell>
  );
}

// ─── Bonificaciones ───────────────────────────────────────────────────────────
export function BonificacionesSection({ value, detalle, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [monto,    setMonto]    = useState(value || 0);
  const [concepto, setConcepto] = useState(detalle?.concepto || '');

  const handleMontoChange    = (v) => { setMonto(v); onUpdate?.(v, { concepto }); };
  const handleConceptoChange = (v) => { setConcepto(v); onUpdate?.(monto, { concepto: v }); };

  return (
    <SectionShell
      icon={Gift} iconCls="text-pink-500"
      title="Bono por Desempeño" subtitle="Bonos adicionales por resultados"
      value={value} isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Monto (RD$)</label>
          <input
            type="number" step="0.01" min="0"
            value={monto || ''} placeholder="0.00"
            onChange={(e) => handleMontoChange(parseFloat(e.target.value) || 0)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Concepto (opcional)</label>
          <input
            type="text" value={concepto}
            onChange={(e) => handleConceptoChange(e.target.value)}
            placeholder="Ej: Bonificación anual 2025"
            className={inputCls}
          />
        </div>
      </div>
    </SectionShell>
  );
}

// ─── Otros Ingresos ───────────────────────────────────────────────────────────
export function OtrosIngresosSection({ value, detalle, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [monto,    setMonto]    = useState(value || 0);
  const [concepto, setConcepto] = useState(detalle?.concepto || '');

  const handleMontoChange    = (v) => { setMonto(v); onUpdate?.(v, { concepto }); };
  const handleConceptoChange = (v) => { setConcepto(v); onUpdate?.(monto, { concepto: v }); };

  return (
    <SectionShell
      icon={DollarSign} iconCls="text-gray-400"
      title="Otros Ingresos" subtitle="Reembolsos, viáticos, otros"
      value={value} isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Monto (RD$)</label>
          <input
            type="number" step="0.01" min="0"
            value={monto || ''} placeholder="0.00"
            onChange={(e) => handleMontoChange(parseFloat(e.target.value) || 0)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Concepto (opcional)</label>
          <input
            type="text" value={concepto}
            onChange={(e) => handleConceptoChange(e.target.value)}
            placeholder="Ej: Reembolso gastos médicos"
            className={inputCls}
          />
        </div>
      </div>
    </SectionShell>
  );
}
